import React, { useEffect, useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCloud,
  faDownload,
  faEject,
  faFloppyDisk,
  faFolderOpen,
  faGlobe,
  faLayerGroup,
  faLock,
  faPause,
  faRotate,
  faStar,
  faSync
} from "@fortawesome/free-solid-svg-icons"
import "../disk/diskinterface.css"
import PopupMenu from "../../controls/popupmenu"
import {
  handleGetVeraSdStatus,
  subscribeVeraSdStatus,
  handleSetVeraSdImage,
  handleGetSlotConfig,
  requestVeraSdImage,
  handleSetVeraSdWriteProtected,
  handleClearVeraSdChanges
} from "../../main2worker"
import { getPreferenceSlotConfig } from "../../localstorage"
import { SETTINGS_CHANGED_EVENT } from "../../settingschange"
import { isFileSystemApiSupported } from "../../ui_utilities"
import { toHex, VeraSdStatus, getPrimaryDiskUrl, getCleanDiskFilename, parseDiskSwapperParam, separateUrlAndComment, getBlobFromDiskData } from "../../../common/utility"
import { useTranslation } from "../../../i18n/useTranslation"
import { OneDriveCloudDrive } from "../disk/onedriveclouddrive"
import { GoogleDrive } from "../disk/googledrive"
import { findMatchingDiskImage, handleSetDiskOrFileFromBuffer, fetchWithCorsProxy, setStandbyDisks } from "../disk/driveprops"
import { decompressAllDiskFiles } from "../../../common/zip_utils"

export interface StandbyVeraSd {
  filename: string
  diskData: Uint8Array
  diskHasChanges?: boolean
  writeProtected?: boolean
  writableFileHandle?: FileSystemFileHandle | null
  comment?: string
}

let standbyVeraDisks: StandbyVeraSd[] = []
let standbyVeraActiveIndex = 0
const standbyVeraListeners = new Set<(disks: StandbyVeraSd[], activeIndex: number) => void>()

export const getStandbyVeraDisks = (): StandbyVeraSd[] => standbyVeraDisks
export const getStandbyVeraActiveIndex = (): number => standbyVeraActiveIndex

export const subscribeStandbyVeraDisks = (listener: (disks: StandbyVeraSd[], activeIndex: number) => void): (() => void) => {
  standbyVeraListeners.add(listener)
  return () => { standbyVeraListeners.delete(listener) }
}

const notifyStandbyVeraChange = () => {
  standbyVeraListeners.forEach(l => l(standbyVeraDisks, standbyVeraActiveIndex))
}

export const setStandbyVeraDisks = (disks: StandbyVeraSd[], activeIndex = 0) => {
  standbyVeraDisks = [...disks]
  standbyVeraActiveIndex = activeIndex
  notifyStandbyVeraChange()
}

export const appendStandbyVeraDisks = (disks: StandbyVeraSd[]) => {
  const existingNames = new Set(standbyVeraDisks.map(d => d.filename.toLowerCase()))
  const toAdd = disks.filter(d => !existingNames.has(d.filename.toLowerCase()))
  if (toAdd.length > 0) {
    standbyVeraDisks = [...standbyVeraDisks, ...toAdd]
    notifyStandbyVeraChange()
  }
}

export const syncActiveVeraDiskToStandby = async () => {
  if (standbyVeraDisks[standbyVeraActiveIndex]) {
    const result = await requestVeraSdImage()
    if (result && result.data) {
      standbyVeraDisks[standbyVeraActiveIndex].diskData = result.data
      standbyVeraDisks[standbyVeraActiveIndex].diskHasChanges = false
    }
  }
}

export const swapVeraStandbyDisk = async (targetIndex: number) => {
  if (targetIndex < 0 || targetIndex >= standbyVeraDisks.length) return
  if (targetIndex === standbyVeraActiveIndex) return

  const curStatus = handleGetVeraSdStatus()
  if (curStatus.hasChanges) {
    if (currentVeraSdFileHandle) {
      await saveVeraSdToDevice()
    } else {
      await syncActiveVeraDiskToStandby()
    }
  }

  standbyVeraActiveIndex = targetIndex
  const target = standbyVeraDisks[targetIndex]
  setVeraSdFileHandle(target.writableFileHandle || null)
  setVeraSdCloudData(null)
  handleSetVeraSdImage(target.diskData, target.filename)
  if (target.writeProtected !== undefined) {
    handleSetVeraSdWriteProtected(target.writeProtected)
  }
  notifyStandbyVeraChange()
}

export const removeStandbyVeraDisk = async (indexToRemove: number) => {
  if (indexToRemove < 0 || indexToRemove >= standbyVeraDisks.length) return
  if (indexToRemove === standbyVeraActiveIndex) {
    if (standbyVeraDisks.length <= 1) {
      ejectVeraSd()
      return
    }
    const nextIdx = indexToRemove === 0 ? 1 : indexToRemove - 1
    await swapVeraStandbyDisk(nextIdx)
  }
  standbyVeraDisks.splice(indexToRemove, 1)
  if (standbyVeraActiveIndex > indexToRemove) {
    standbyVeraActiveIndex--
  }
  notifyStandbyVeraChange()
}

export const handleSetVeraSdFromURL = async (url: string): Promise<boolean> => {
  try {
    const urls = parseDiskSwapperParam(url)
    const fetchedDisks: StandbyVeraSd[] = []

    for (const singleUrl of urls) {
      const { url: rawUrl, comment } = separateUrlAndComment(singleUrl)
      let resolvedUrl = rawUrl
      const isLocalFile = !rawUrl.startsWith("http://") && !rawUrl.startsWith("https://")
      if (isLocalFile && !rawUrl.startsWith("/") && !rawUrl.startsWith("file://")) {
        const match = findMatchingDiskImage(rawUrl)
        const matchUrl = getPrimaryDiskUrl(match)
        if (matchUrl) {
          resolvedUrl = matchUrl
        }
        if (!URL.canParse(resolvedUrl) && !resolvedUrl.startsWith("/")) {
          resolvedUrl = "disks/" + resolvedUrl
        }
      }
      let response: Response | null = null
      try {
        response = await fetch(resolvedUrl)
        if (!response.ok) response = null
      } catch {
        response = null
      }
      if (!response || !response.ok) {
        response = await fetchWithCorsProxy(resolvedUrl)
      }
      if ((!response || !response.ok) && typeof window !== "undefined" && /\.pages\.dev$/i.test(window.location.hostname)) {
        try {
          response = await fetch(`/api/disk-direct?url=${encodeURIComponent(resolvedUrl)}`)
        } catch {
          response = null
        }
      }
      if (!response || !response.ok) continue
      const buffer = await response.arrayBuffer()
      const fileName = decodeURIComponent(resolvedUrl.split("/").pop()?.split("?")[0] || "sd.img")

      const decompressedList = await decompressAllDiskFiles(
        buffer,
        fileName,
        [".img", ".raw", ".sd", ".bin", ".iso", ".dsk", ".woz", ".po", ".do", ".2mg", ".hdv"]
      )
      if (decompressedList && decompressedList.length > 0) {
        for (const item of decompressedList) {
          fetchedDisks.push({
            filename: getCleanDiskFilename(item.filename),
            diskData: item.data,
            comment,
          })
        }
      } else {
        fetchedDisks.push({
          filename: getCleanDiskFilename(fileName),
          diskData: new Uint8Array(buffer),
          comment,
        })
      }
    }

    if (fetchedDisks.length === 0) return false

    if (isAppleDiskImage(fetchedDisks[0].filename)) {
      handleSetDiskOrFileFromBuffer(0, fetchedDisks[0].diskData.buffer as ArrayBuffer, fetchedDisks[0].filename, null, null)
      if (fetchedDisks.length > 1) {
        setStandbyDisks(0, fetchedDisks.map(d => ({
          filename: d.filename,
          diskData: d.diskData,
          diskHasChanges: false,
          isWriteProtected: false,
          writableFileHandle: null,
          cloudData: null,
          lastAppleWriteTime: -1,
          lastLocalFileWriteTime: -1,
          comment: d.comment,
        })), 0)
      }
      return true
    }

    const first = fetchedDisks[0]
    setVeraSdFileHandle(null)
    setVeraSdCloudData(null)
    handleSetVeraSdImage(first.diskData, first.filename)
    setStandbyVeraDisks(fetchedDisks, 0)
    return true
  } catch (error) {
    console.error("Error loading VERA SD from URL:", error)
    return false
  }
}

export const isAppleDiskImage = (filename: string): boolean => {
  const lower = filename.toLowerCase()
  const diskExts = [".dsk", ".woz", ".do", ".po", ".2mg", ".hdv", ".2meg", ".nib"]
  return diskExts.some(ext => lower.endsWith(ext))
}

export interface VeraSdIconProps {
  renderCount?: number
}

const isVeraInstalledInSlot = (): boolean => {
  const prefConfig = getPreferenceSlotConfig()
  const workerConfig = handleGetSlotConfig()
  return prefConfig[2] === "vera" || prefConfig[4] === "vera" || workerConfig[2] === "vera" || workerConfig[4] === "vera"
}

let currentVeraSdFileHandle: FileSystemFileHandle | null = null

export const setVeraSdFileHandle = (handle: FileSystemFileHandle | null) => {
  currentVeraSdFileHandle = handle
}

export const getVeraSdFileHandle = () => currentVeraSdFileHandle

export const downloadSdImage = (diskData: Uint8Array, filename: string) => {
  const blob = new Blob([diskData] as BlobPart[], { type: "application/octet-stream" })
  const link = document.createElement("a")
  const url = URL.createObjectURL(blob)
  link.setAttribute("href", url)
  link.setAttribute("download", filename || "sd.img")
  link.style.visibility = "hidden"
  document.body.appendChild(link)
  link.click()
  setTimeout(() => {
    if (document.body.contains(link)) {
      document.body.removeChild(link)
    }
    URL.revokeObjectURL(url)
  }, 10000)
}

export const openSdFilePicker = async (append = false) => {
  if (isVeraSdSyncing()) return
  if (isFileSystemApiSupported()) {
    try {
      const fileHandles = await window.showOpenFilePicker({
        types: [
          {
            description: "SD Card & Disk Images (*.img, *.raw, *.sd, *.bin, *.dsk, *.woz, *.zip, *.gz, *.7z)",
            accept: {
              "application/octet-stream": [".img", ".raw", ".sd", ".bin", ".iso", ".dsk", ".woz", ".po", ".do", ".2mg", ".hdv", ".zip", ".gz", ".7z"]
            }
          }
        ],
        excludeAcceptAllOption: false,
        multiple: true
      })
      if (fileHandles && fileHandles.length > 0) {
        const newDisks: StandbyVeraSd[] = []
        for (const fileHandle of fileHandles) {
          const file = await fileHandle.getFile()
          const buffer = await file.arrayBuffer()
          const decompressedList = await decompressAllDiskFiles(
            buffer,
            file.name,
            [".img", ".raw", ".sd", ".bin", ".iso", ".dsk", ".woz", ".po", ".do", ".2mg", ".hdv"]
          )
          if (decompressedList && decompressedList.length > 0) {
            for (const item of decompressedList) {
              newDisks.push({
                filename: getCleanDiskFilename(item.filename),
                diskData: item.data,
                writableFileHandle: null,
              })
            }
          } else {
            newDisks.push({
              filename: getCleanDiskFilename(file.name),
              diskData: new Uint8Array(buffer),
              writableFileHandle: fileHandle,
            })
          }
        }
        if (newDisks.length === 0) return

        newDisks.sort((a, b) =>
          a.filename.localeCompare(b.filename, undefined, { numeric: true, sensitivity: "base" })
        )

        if (isAppleDiskImage(newDisks[0].filename)) {
          handleSetDiskOrFileFromBuffer(0, newDisks[0].diskData.buffer as ArrayBuffer, newDisks[0].filename, null, newDisks[0].writableFileHandle || null)
          if (newDisks.length > 1) {
            setStandbyDisks(0, newDisks.map(d => ({
              filename: d.filename,
              diskData: d.diskData,
              diskHasChanges: false,
              isWriteProtected: false,
              writableFileHandle: d.writableFileHandle || null,
              cloudData: null,
              lastAppleWriteTime: -1,
              lastLocalFileWriteTime: -1,
            })), 0)
          }
          return
        }

        if (append) {
          appendStandbyVeraDisks(newDisks)
        } else {
          const first = newDisks[0]
          setVeraSdCloudData(null)
          setVeraSdFileHandle(first.writableFileHandle || null)
          handleSetVeraSdImage(first.diskData, first.filename)
          setStandbyVeraDisks(newDisks, 0)
        }
        return
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "AbortError") {
        return // User cancelled
      }
      console.warn("showOpenFilePicker error, falling back to input element:", err)
    }
  }

  // Fallback for browsers without File System Access API
  const input = document.createElement("input")
  input.type = "file"
  input.multiple = true
  input.accept = ".img,.raw,.sd,.bin,.iso,.dsk,.woz,.po,.do,.2mg,.hdv,.zip,.gz,.7z"
  input.style.position = "fixed"
  input.style.left = "-9999px"
  input.style.top = "-9999px"
  input.style.opacity = "0"
  document.body.appendChild(input)

  input.onchange = async () => {
    try {
      if (input.files && input.files.length > 0) {
        const newDisks: StandbyVeraSd[] = []
        for (const file of Array.from(input.files)) {
          const buffer = await file.arrayBuffer()
          const decompressedList = await decompressAllDiskFiles(
            buffer,
            file.name,
            [".img", ".raw", ".sd", ".bin", ".iso", ".dsk", ".woz", ".po", ".do", ".2mg", ".hdv"]
          )
          if (decompressedList && decompressedList.length > 0) {
            for (const item of decompressedList) {
              newDisks.push({
                filename: getCleanDiskFilename(item.filename),
                diskData: item.data,
                writableFileHandle: null,
              })
            }
          } else {
            newDisks.push({
              filename: getCleanDiskFilename(file.name),
              diskData: new Uint8Array(buffer),
              writableFileHandle: null,
            })
          }
        }
        if (newDisks.length === 0) return

        newDisks.sort((a, b) =>
          a.filename.localeCompare(b.filename, undefined, { numeric: true, sensitivity: "base" })
        )

        if (isAppleDiskImage(newDisks[0].filename)) {
          handleSetDiskOrFileFromBuffer(0, newDisks[0].diskData.buffer as ArrayBuffer, newDisks[0].filename, null, null)
          if (newDisks.length > 1) {
            setStandbyDisks(0, newDisks.map(d => ({
              filename: d.filename,
              diskData: d.diskData,
              diskHasChanges: false,
              isWriteProtected: false,
              writableFileHandle: null,
              cloudData: null,
              lastAppleWriteTime: -1,
              lastLocalFileWriteTime: -1,
            })), 0)
          }
          return
        }

        if (append) {
          appendStandbyVeraDisks(newDisks)
        } else {
          const first = newDisks[0]
          setVeraSdCloudData(null)
          setVeraSdFileHandle(null)
          handleSetVeraSdImage(first.diskData, first.filename)
          setStandbyVeraDisks(newDisks, 0)
        }
      }
    } finally {
      if (document.body.contains(input)) {
        document.body.removeChild(input)
      }
    }
  }

  input.click()
}

let currentVeraSdCloudData: CloudData | null = null
const cloudDataListeners = new Set<(data: CloudData | null) => void>()
export const getVeraSdCloudData = () => currentVeraSdCloudData
export const setVeraSdCloudData = (data: CloudData | null) => {
  currentVeraSdCloudData = data
  cloudDataListeners.forEach(l => l(data))
}

let veraSdSyncPaused = false
const syncPausedListeners = new Set<(paused: boolean) => void>()
export const isVeraSdSyncPaused = () => veraSdSyncPaused
export const setVeraSdSyncPaused = (paused: boolean) => {
  veraSdSyncPaused = paused
  syncPausedListeners.forEach(l => l(paused))
}
export const toggleVeraSdSyncPaused = () => setVeraSdSyncPaused(!veraSdSyncPaused)

let veraSdSyncing = false
const syncingListeners = new Set<(syncing: boolean) => void>()
export const isVeraSdSyncing = () => veraSdSyncing
export const setVeraSdSyncing = (syncing: boolean) => {
  veraSdSyncing = syncing
  syncingListeners.forEach(l => l(syncing))
}

export const showSdSaveFilePicker = async () => {
  const result = await requestVeraSdImage()
  if (!result || !result.data) return
  const fileName = result.name || "sd.img"
  const fileExtension = fileName.includes(".") ? fileName.substring(fileName.lastIndexOf(".")) : ".img"
  try {
    const writableFileHandle = await window.showSaveFilePicker({
      excludeAcceptAllOption: false,
      suggestedName: fileName,
      types: [
        {
          description: "Disk Image",
          accept: { "application/octet-stream": [fileExtension] as `.${string}`[] },
        },
      ]
    })
    if (writableFileHandle) {
      setVeraSdCloudData(null)
      setVeraSdFileHandle(writableFileHandle)
      const writable = await writableFileHandle.createWritable()
      const blob = getBlobFromDiskData(result.data, fileName)
      await writable.write(blob)
      await writable.close()
      handleClearVeraSdChanges(result.writeSeq)
    }
  } catch (err: unknown) {
    if (err instanceof Error && err.name !== "AbortError") {
      console.warn("Save file picker error, falling back to download:", err)
      downloadSdImage(result.data, fileName)
      handleClearVeraSdChanges(result.writeSeq)
    }
  }
}

export const saveVeraSdToDevice = async () => {
  const result = await requestVeraSdImage()
  if (!result || !result.data) return
  if (currentVeraSdFileHandle) {
    try {
      const writable = await currentVeraSdFileHandle.createWritable()
      const blob = getBlobFromDiskData(result.data, currentVeraSdFileHandle.name || "image.woz")
      await writable.write(blob)
      await writable.close()
      handleClearVeraSdChanges(result.writeSeq)
      return
    } catch (err) {
      console.warn("Direct write to file handle failed, falling back to picker:", err)
    }
  }
  await showSdSaveFilePicker()
}

export const loadVeraSdFromCloud = async (cloudProvider: CloudProvider) => {
  if (isVeraSdSyncing()) return
  try {
    const result = await cloudProvider.download(".img,.raw,.sd,.bin,.iso,.dsk,.woz,.po,.do,.2mg,.hdv")
    if (result) {
      const [blob, data] = Array.isArray(result) && Array.isArray(result[0])
        ? (result as Array<[Blob, CloudData]>)[0]
        : (result as [Blob, CloudData])
      const buffer = await new Response(blob).arrayBuffer()
      if (isAppleDiskImage(data.fileName)) {
        handleSetDiskOrFileFromBuffer(0, buffer, data.fileName, data, null)
        return
      }
      setVeraSdFileHandle(null)
      setVeraSdCloudData(data)
      handleSetVeraSdImage(new Uint8Array(buffer), data.fileName)
    }
  } catch (err) {
    console.warn("Cloud download failed:", err)
  }
}

export const saveVeraSdToCloud = async (cloudProvider: CloudProvider) => {
  setVeraSdSyncing(true)
  const startTime = Date.now()
  try {
    const result = await requestVeraSdImage()
    if (!result || !result.data) return
    const blob = new Blob([result.data] as BlobPart[], { type: "application/octet-stream" })
    const uploaded = await cloudProvider.upload(result.name || "sd.img", blob)
    if (uploaded) {
      setVeraSdFileHandle(null)
      setVeraSdCloudData(uploaded)
      handleClearVeraSdChanges(result.writeSeq)
    }
  } catch (err) {
    console.warn("Cloud upload failed:", err)
  } finally {
    const elapsed = Date.now() - startTime
    const remaining = Math.max(0, 1500 - elapsed)
    setTimeout(() => {
      setVeraSdSyncing(false)
    }, remaining)
  }
}

export const syncVeraSdNow = async () => {
  setVeraSdSyncing(true)
  const startTime = Date.now()
  try {
    const result = await requestVeraSdImage()
    if (!result || !result.data) return

    if (currentVeraSdFileHandle) {
      try {
        const writable = await currentVeraSdFileHandle.createWritable()
        const blob = getBlobFromDiskData(result.data, currentVeraSdFileHandle.name || "image.woz")
        await writable.write(blob)
        await writable.close()
        handleClearVeraSdChanges(result.writeSeq)
        return
      } catch (err) {
        console.warn("Sync to local file failed, trying cloud/picker fallback:", err)
      }
    }

    if (currentVeraSdCloudData) {
      const blob = new Blob([result.data] as BlobPart[], { type: "application/octet-stream" })
      const provider = currentVeraSdCloudData.providerName === "OneDrive" ? new OneDriveCloudDrive() : new GoogleDrive()
      const success = await provider.sync(blob, currentVeraSdCloudData)
      if (success) {
        handleClearVeraSdChanges(result.writeSeq)
        return
      }
    }

    await showSdSaveFilePicker()
  } finally {
    const elapsed = Date.now() - startTime
    const remaining = Math.max(0, 1500 - elapsed)
    setTimeout(() => {
      setVeraSdSyncing(false)
    }, remaining)
  }
}

export const downloadVeraSd = async () => {
  const result = await requestVeraSdImage()
  if (result && result.data) {
    downloadSdImage(result.data, result.name)
  }
}

export const ejectVeraSd = () => {
  if (isVeraSdSyncing()) return
  setVeraSdFileHandle(null)
  setVeraSdCloudData(null)
  setVeraSdSyncPaused(false)
  setVeraSdSyncing(false)
  standbyVeraDisks = []
  standbyVeraActiveIndex = 0
  notifyStandbyVeraChange()
  handleSetVeraSdImage(null)
}

export const downloadAndEjectVeraSd = async () => {
  if (isVeraSdSyncing()) return
  const result = await requestVeraSdImage()
  if (result && result.data) {
    downloadSdImage(result.data, result.name)
    handleClearVeraSdChanges(result.writeSeq)
  }
  ejectVeraSd()
}

export const VeraSdIcon: React.FC<VeraSdIconProps> = () => {
  const { t, language } = useTranslation()
  const [status, setStatus] = useState<VeraSdStatus>(handleGetVeraSdStatus())
  const [installed, setInstalled] = useState<boolean>(isVeraInstalledInSlot)
  const [popupLocation, setPopupLocation] = useState<[number, number] | undefined>(undefined)
  const [isSyncPaused, setIsSyncPaused] = useState<boolean>(isVeraSdSyncPaused)
  const [isSyncing, setIsSyncing] = useState<boolean>(isVeraSdSyncing)
  const [standbyDisks, setStandbyDisksState] = useState<StandbyVeraSd[]>(getStandbyVeraDisks)
  const [standbyActiveIndex, setStandbyActiveIndexState] = useState<number>(getStandbyVeraActiveIndex)
  const [swapperPopupLocation, setSwapperPopupLocation] = useState<[number, number] | undefined>(undefined)

  useEffect(() => {
    return subscribeStandbyVeraDisks((disks, activeIndex) => {
      setStandbyDisksState([...disks])
      setStandbyActiveIndexState(activeIndex)
    })
  }, [])

  useEffect(() => {
    return subscribeVeraSdStatus((newStatus) => {
      setStatus({ ...newStatus })
    })
  }, [])

  useEffect(() => {
    const onPause = (p: boolean) => setIsSyncPaused(p)
    const onSync = (s: boolean) => setIsSyncing(s)
    syncPausedListeners.add(onPause)
    syncingListeners.add(onSync)
    return () => {
      syncPausedListeners.delete(onPause)
      syncingListeners.delete(onSync)
    }
  }, [])

  useEffect(() => {
    const handleSettings = () => {
      setInstalled(isVeraInstalledInSlot())
    }
    window.addEventListener(SETTINGS_CHANGED_EVENT, handleSettings)
    window.addEventListener("apple2ts-slot-config-changed", handleSettings)
    return () => {
      window.removeEventListener(SETTINGS_CHANGED_EVENT, handleSettings)
      window.removeEventListener("apple2ts-slot-config-changed", handleSettings)
    }
  }, [])

  const slotConfig = getPreferenceSlotConfig()
  const workerConfig = handleGetSlotConfig()
  const veraSlot = (slotConfig[4] === "vera" || workerConfig[4] === "vera") ? 4 : 2
  const isTouchDevice = typeof window !== "undefined" && "ontouchstart" in document.documentElement
  const isElectron = typeof navigator !== "undefined" && navigator.userAgent.includes("Electron")
  const isEnglish = (language || "en").startsWith("en")

  const validStandbyDisks = standbyDisks.filter(d => Boolean(d?.filename?.trim()))
  const hasMultipleDisks = validStandbyDisks.length > 1

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (popupLocation) {
      setPopupLocation(undefined)
      return
    }
    setPopupLocation([e.clientX, e.clientY])
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (isVeraSdSyncing()) return
    const files = Array.from(e.dataTransfer.files || [])
    if (files.length === 0) return

    const newDisks: StandbyVeraSd[] = []
    for (const file of files) {
      const buffer = await file.arrayBuffer()
      const decompressedList = await decompressAllDiskFiles(
        buffer,
        file.name,
        [".img", ".raw", ".sd", ".bin", ".iso", ".dsk", ".woz", ".po", ".do", ".2mg", ".hdv"]
      )
      if (decompressedList && decompressedList.length > 0) {
        for (const item of decompressedList) {
          newDisks.push({
            filename: getCleanDiskFilename(item.filename),
            diskData: item.data,
            writableFileHandle: null,
          })
        }
      } else {
        newDisks.push({
          filename: getCleanDiskFilename(file.name),
          diskData: new Uint8Array(buffer),
          writableFileHandle: null,
        })
      }
    }
    if (newDisks.length === 0) return

    newDisks.sort((a, b) =>
      a.filename.localeCompare(b.filename, undefined, { numeric: true, sensitivity: "base" })
    )

    if (isAppleDiskImage(newDisks[0].filename)) {
      handleSetDiskOrFileFromBuffer(0, newDisks[0].diskData.buffer as ArrayBuffer, newDisks[0].filename, null, null)
      if (newDisks.length > 1) {
        setStandbyDisks(0, newDisks.map(d => ({
          filename: d.filename,
          diskData: d.diskData,
          diskHasChanges: false,
          isWriteProtected: false,
          writableFileHandle: null,
          cloudData: null,
          lastAppleWriteTime: -1,
          lastLocalFileWriteTime: -1,
        })), 0)
      }
      return
    }

    const first = newDisks[0]
    setVeraSdCloudData(null)
    setVeraSdFileHandle(null)
    handleSetVeraSdImage(first.diskData, first.filename)
    setStandbyVeraDisks(newDisks, 0)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const sizeFormatted = status.attached
    ? status.size >= 1024 * 1024
      ? `${(status.size / (1024 * 1024)).toFixed(1)} MB`
      : `${(status.size / 1024).toFixed(0)} KB`
    : ""

  const titleText = status.attached
    ? `VERA SD: ${status.name}${status.hasChanges ? ` (${t("disk.modified")})` : ""}${status.writeProtected ? ` [${t("disk.writeProtectDisk")}]` : ""} (${sizeFormatted})${status.lba !== undefined ? ` [LBA ${toHex(status.lba, status.lba > 0xFFFF ? 6 : 4)}]` : ""} - Click for options`
    : "VERA SD: Raw SPI mass storage card (*.img, *.raw, *.bin, *.sd) - Click to load or drop file (Floppy/HDD images .dsk/.woz/.po/.hdv auto-boot)"

  const lbaFormatted = status.lba !== undefined
    ? ` ${toHex(status.lba, status.lba > 0xFFFF ? 6 : 4)}`
    : ""
  const statusText = `S${veraSlot},SD${lbaFormatted}`

  // When disk has changes, turn filename red and prefix with *
  const diskLabelClass = `disk-label${status.hasChanges ? " disk-label-unsaved" : ""}${isTouchDevice ? " disk-label-small" : ""}`

  // Background auto-save to local file handle when not paused
  useEffect(() => {
    if (!status.attached || !status.hasChanges || isSyncPaused || !currentVeraSdFileHandle) {
      return
    }
    const timer = setTimeout(async () => {
      if (isSyncPaused || !currentVeraSdFileHandle) return
      setVeraSdSyncing(true)
      const startTime = Date.now()
      try {
        const result = await requestVeraSdImage()
        if (result && result.data && currentVeraSdFileHandle) {
          const writable = await currentVeraSdFileHandle.createWritable()
          const blob = getBlobFromDiskData(result.data, currentVeraSdFileHandle.name || "image.woz")
          await writable.write(blob)
          await writable.close()
          handleClearVeraSdChanges(result.writeSeq)
        }
      } catch (err) {
        console.warn("Auto-save to local file failed:", err)
      } finally {
        const elapsed = Date.now() - startTime
        const remaining = Math.max(0, 1500 - elapsed)
        setTimeout(() => {
          setVeraSdSyncing(false)
        }, remaining)
      }
    }, 2000)
    return () => clearTimeout(timer)
  }, [status.attached, status.hasChanges, isSyncPaused])

  const menuItems = useMemo(() => {
    const loadDiskSubMenu = [
      {
        label: t("disk.OneDrive"),
        icon: faCloud,
        isDisabled: isSyncing,
        isVisible: () => !isElectron,
        onClick: () => {
          setPopupLocation(undefined)
          void loadVeraSdFromCloud(new OneDriveCloudDrive())
        }
      },
      {
        label: t("disk.GoogleDrive"),
        icon: faCloud,
        isDisabled: isSyncing,
        isVisible: () => !isElectron,
        onClick: () => {
          setPopupLocation(undefined)
          void loadVeraSdFromCloud(new GoogleDrive())
        }
      },
    ]

    const saveDiskSubMenu = [
      {
        label: t("disk.OneDrive"),
        icon: faCloud,
        isDisabled: isSyncing,
        onClick: () => {
          setPopupLocation(undefined)
          void saveVeraSdToCloud(new OneDriveCloudDrive())
        }
      },
      {
        label: t("disk.GoogleDrive"),
        icon: faCloud,
        isDisabled: isSyncing,
        onClick: () => {
          setPopupLocation(undefined)
          void saveVeraSdToCloud(new GoogleDrive())
        }
      },
    ]

    return [[
    // 1. 載入磁碟 (Load Disk)
    {
      label: isEnglish ? "Load Disk(s)..." : t("disk.loadDisk"),
      icon: faFolderOpen,
      isDisabled: isSyncing,
      onClick: () => {
        setPopupLocation(undefined)
        setTimeout(() => {
          openSdFilePicker(false)
        }, 50)
      }
    },
    {
      label: t("disk.loadAdditionalDisks"),
      icon: faLayerGroup,
      isDisabled: !status.attached || isSyncing,
      onClick: () => {
        setPopupLocation(undefined)
        setTimeout(() => {
          openSdFilePicker(true)
        }, 50)
      }
    },
    // 2. 載入磁碟自 (Load Disk From)
    {
      label: t("disk.loadDiskFrom"),
      icon: faGlobe,
      isDisabled: isSyncing,
      subMenu: loadDiskSubMenu
    },
    {
      label: "-"
    },
    // 3. 磁碟防寫保護 (Write-protect Disk)
    {
      label: t("disk.writeProtectDisk"),
      icon: faLock,
      isDisabled: !status.attached,
      isSelected: () => !!status.writeProtected,
      onClick: () => {
        setPopupLocation(undefined)
        handleSetVeraSdWriteProtected(!status.writeProtected)
      }
    },
    // 4. 加入收藏 (Add to Favorites - grey out)
    {
      label: t("disk.addToFavorites"),
      icon: faStar,
      isDisabled: true
    },
    {
      label: "-"
    },
    // 5. 下載磁碟 (Download Disk)
    {
      label: (isEnglish && hasMultipleDisks) ? "Download Disk(s)..." : `${t("disk.downloadDisk")}...`,
      icon: faDownload,
      isDisabled: !status.attached,
      onClick: async () => {
        setPopupLocation(undefined)
        await syncActiveVeraDiskToStandby()
        const disksToDownload = hasMultipleDisks ? validStandbyDisks : (
          status.attached ? [{ filename: status.name, diskData: (await requestVeraSdImage())?.data || new Uint8Array() }] : []
        )
        disksToDownload.forEach((d, i) => {
          if (d.diskData && d.diskData.length > 0) {
            setTimeout(() => {
              downloadSdImage(d.diskData, d.filename)
            }, i * 200)
          }
        })
      }
    },
    // 6. 下載 WOZ 磁碟映像 (Download WOZ Disk Image - grey out)
    {
      label: t("disk.downloadWoz"),
      icon: faDownload,
      isDisabled: true
    },
    // 7. 下載並退出磁碟 (Download & Eject Disk)
    {
      label: t("disk.downloadAndEjectDisk"),
      icon: faDownload,
      isDisabled: !status.attached || isSyncing,
      onClick: async () => {
        setPopupLocation(undefined)
        downloadAndEjectVeraSd()
      }
    },
    // 8. 退出磁碟 (Eject Disk)
    {
      label: (isEnglish && hasMultipleDisks) ? "Eject Disk(s)" : t("disk.ejectDisk"),
      icon: faEject,
      isDisabled: !status.attached || isSyncing,
      subMenu: hasMultipleDisks
        ? [
            {
              label: t("disk.ejectAllDisks"),
              onClick: () => {
                setPopupLocation(undefined)
                ejectVeraSd()
              }
            },
            {
              label: "-"
            },
            ...validStandbyDisks.map((disk, idx) => {
              const cleanFilename = getCleanDiskFilename(disk.filename)
              const displayName = disk.comment ? `${cleanFilename} (${disk.comment})` : cleanFilename
              return {
                label: `Eject: ${displayName}`,
                onClick: () => {
                  setPopupLocation(undefined)
                  removeStandbyVeraDisk(idx)
                }
              }
            })
          ]
        : undefined,
      onClick: !hasMultipleDisks ? () => {
        setPopupLocation(undefined)
        ejectVeraSd()
      } : undefined,
    },
    {
      label: "-"
    },
    // 9. 儲存磁碟 (Save Disk to Device)
    {
      label: (isEnglish && hasMultipleDisks) ? "Save Disk(s)" : t("disk.saveDiskToDevice"),
      icon: faFloppyDisk,
      isDisabled: !status.attached || isSyncing,
      isVisible: () => isFileSystemApiSupported(),
      onClick: async () => {
        setPopupLocation(undefined)
        if (!hasMultipleDisks) {
          await saveVeraSdToDevice()
        } else {
          await syncActiveVeraDiskToStandby()
          for (let i = 0; i < validStandbyDisks.length; i++) {
            const disk = validStandbyDisks[i]
            const fileExtension = disk.filename.includes(".") ? disk.filename.substring(disk.filename.lastIndexOf(".")) : ".img"
            try {
              const writableFileHandle = disk.writableFileHandle || await window.showSaveFilePicker({
                excludeAcceptAllOption: false,
                suggestedName: disk.filename,
                types: [{
                  description: t("disk.diskImage"),
                  accept: { "application/octet-stream": [fileExtension] as `.${string}`[] },
                }],
              })
              if (writableFileHandle) {
                // eslint-disable-next-line react-hooks/immutability
                disk.writableFileHandle = writableFileHandle
                const writable = await writableFileHandle.createWritable()
                const blob = new Blob([disk.diskData as BlobPart], { type: "application/octet-stream" })
                await writable.write(blob)
                await writable.close()
              }
            } catch {
              break
            }
          }
          const res = await requestVeraSdImage()
          if (res) {
            handleClearVeraSdChanges(res.writeSeq)
          }
        }
      }
    },
    // 10. 儲存磁碟到 (Save Disk To)
    {
      label: t("disk.saveDiskTo"),
      icon: faGlobe,
      isDisabled: !status.attached || isSyncing,
      isVisible: () => !isElectron,
      subMenu: saveDiskSubMenu
    },
    // 11. 暫停同步 (Pause Syncing)
    {
      label: t("disk.pauseSyncing"),
      icon: faPause,
      isDisabled: !status.attached,
      isSelected: () => isSyncPaused,
      onClick: () => {
        setPopupLocation(undefined)
        toggleVeraSdSyncPaused()
      }
    },
    // 12. 立即同步 (Sync Now)
    {
      label: t("disk.syncNow"),
      icon: faSync,
      isDisabled: !status.attached || isSyncing,
      onClick: () => {
        setPopupLocation(undefined)
        syncVeraSdNow()
      }
    }
  ]]}, [status, isElectron, t, isEnglish, hasMultipleDisks, validStandbyDisks, isSyncPaused, isSyncing])

  // If VERA is not in slot 2 or 4, do not render at all
  if (!installed) {
    return null
  }

  return (
    <>
      <span
        className="flex-column"
        style={{
          cursor: "pointer",
          position: "relative",
          userSelect: "none"
        }}
        title={titleText}
        onClick={handleClick}
        onContextMenu={handleClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <span className="flex-row">
          <span className="flex-column" style={{ position: "relative" }}>
            <svg
              className={`disk-image${isTouchDevice ? " disk-image-small" : ""}`}
              viewBox="0 0 85 55"
            >
              {/* Enclosure body (S7-style Apple II peripheral chassis) */}
              <rect x="1" y="1" width="83" height="53" rx="4" fill="#a7a28e" stroke="#6d6859" strokeWidth="1" />
              <rect x="2" y="2" width="81" height="51" rx="3" fill="none" stroke="#c2bda9" strokeWidth="0.8" opacity="0.6" />

              {/* Vertical seam line (identical to S7 hard drive) */}
              <line x1="54" y1="23" x2="54" y2="53" stroke="#8a8575" strokeWidth="1" />
              <line x1="55" y1="23" x2="55" y2="53" stroke="#bcb7a5" strokeWidth="0.6" />

              {/* Top Black Display Box for Filename (identical to S7 cutout, reserved for .disk-label) */}
              <rect x="4" y="4" width="77" height="18" rx="2.5" fill="#000000" stroke="#5a5547" strokeWidth="0.8" />
              <rect x="4.5" y="4.5" width="76" height="1" fill="#222222" />

              {/* VERA SD Badge */}
              <rect x="7" y="26" width="41" height="13" rx="2" fill="#0284c7" stroke="#0369a1" strokeWidth="0.8" />
              <text
                x="27.5"
                y="35.5"
                textAnchor="middle"
                fill="#ffffff"
                fontSize="7.5"
                fontWeight="bold"
                fontFamily="system-ui, -apple-system, sans-serif"
                letterSpacing="0.8"
              >
                VERA SD
              </text>

              {/* Activity LED recessed tray */}
              <rect x="7" y="42" width="41" height="6" rx="1.5" fill="#787363" stroke="#555042" strokeWidth="0.6" />
              <rect
                x="33"
                y="43"
                width="13"
                height="4"
                rx="1"
                fill={status.attached ? "#22c55e" : "#4b5563"}
                style={status.attached ? { filter: "drop-shadow(0 0 2px #22c55e)" } : undefined}
              />
              {status.attached && (
                <rect x="35" y="44" width="9" height="2" rx="0.5" fill="#86efac" />
              )}

              {/* Right SD Card (standalone, enlarged, without dark background tray) */}
              <path
                d="M 60 26 L 74 26 L 78 30 L 78 48 L 60 48 L 60 37 L 61.5 37 L 61.5 33 L 60 33 Z"
                fill="#1e293b"
                stroke="#0f172a"
                strokeWidth="0.8"
              />
              {/* Gold pins */}
              <line x1="62.5" y1="27.5" x2="62.5" y2="31" stroke="#eab308" strokeWidth="1" />
              <line x1="65" y1="27.5" x2="65" y2="31" stroke="#eab308" strokeWidth="1" />
              <line x1="67.5" y1="27.5" x2="67.5" y2="31" stroke="#eab308" strokeWidth="1" />
              <line x1="70" y1="27.5" x2="70" y2="31" stroke="#eab308" strokeWidth="1" />
              <line x1="72.5" y1="27.5" x2="72.5" y2="31" stroke="#eab308" strokeWidth="1" />
              {/* SD label */}
              <text
                x="69"
                y="43"
                textAnchor="middle"
                fill="#38bdf8"
                fontSize="7.5"
                fontWeight="bold"
                fontFamily="system-ui, -apple-system, sans-serif"
              >
                SD
              </text>
            </svg>
            {hasMultipleDisks && (
              <span
                className="disk-multidisk-badge"
                title={t("disk.standbyDisks")}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  setSwapperPopupLocation([event.clientX, event.clientY])
                }}>
                <FontAwesomeIcon icon={faLayerGroup} />
              </span>
            )}
            {status.attached && (isSyncing ? (
              <FontAwesomeIcon
                icon={faRotate}
                className="fa-fw disk-clouddrive disk-clouddrive-inprogress"
              />
            ) : isSyncPaused ? (
              <FontAwesomeIcon
                icon={faPause}
                className="fa-fw disk-clouddrive disk-clouddrive-paused"
              />
            ) : null)}
          </span>
        </span>

        {/* Disk Filename Label: identical font size and layout as S7 and floppy, red when modified */}
        <span className={diskLabelClass}>
          {status.hasChanges ? "*" : ""}{status.attached ? status.name : ""}
        </span>

        {/* Status Line: identical to S7,D1 (e.g. S2,SD 0800) */}
        <span className="flex-row">
          <span className={`default-font disk-status${isTouchDevice ? " disk-status-small" : ""}`}>
            {statusText}
          </span>
        </span>
      </span>

      {popupLocation && (
        <PopupMenu
          location={popupLocation}
          style={{
            padding: "5px",
            paddingLeft: "10px",
            paddingRight: "10px"
          }}
          onClose={() => setPopupLocation(undefined)}
          menuItems={menuItems}
        />
      )}

      {hasMultipleDisks && (
        <PopupMenu
          location={swapperPopupLocation}
          style={{
            padding: "5px",
            paddingLeft: "10px",
            paddingRight: "10px",
          }}
          onClose={() => {
            setSwapperPopupLocation(undefined)
          }}
          menuItems={[
            validStandbyDisks.map((disk, idx) => {
              const cleanFilename = getCleanDiskFilename(disk.filename)
              const displayName = disk.comment ? `${cleanFilename} (${disk.comment})` : cleanFilename
              return {
                label: displayName,
                icon: faFloppyDisk,
                isSelected: () => idx === standbyActiveIndex,
                onClick: async () => {
                  setSwapperPopupLocation(undefined)
                  if (idx !== standbyActiveIndex) {
                    await swapVeraStandbyDisk(idx)
                  }
                },
              }
            }),
          ]}
        />
      )}
    </>
  )
}

export default VeraSdIcon
